import './styles.scoped.css'
import {useEffect} from 'react'
import {
    Typography,
    Button,
    Modal,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    FormControlLabel,
    Checkbox,
} from '@mui/material'
import {Controller, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'

export const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

const currentYear = new Date().getFullYear()
const years = Array.from({length: 61}, (_, i) => currentYear - i)

const emptyExperience = {
    jobTitle: '',
    company: '',
    startMonth: '',
    startYear: '',
    currentlyWorking: false,
    endMonth: '',
    endYear: '',
}

const toIndex = (year, month) => Number(year) * 12 + Number(month)

// Matches the backend's limits (VARCHAR(250), no future months), so a bad
// entry is caught here rather than failing the whole tab's save.
const MAX_TEXT_LENGTH = 250

const isFuture = (year, month) => {
    const now = new Date()
    return toIndex(year, month) > toIndex(now.getFullYear(), now.getMonth() + 1)
}

const schema = yup.object().shape({
    jobTitle: yup
        .string()
        .trim()
        .required('Job title is required')
        .max(MAX_TEXT_LENGTH, `Job title must be at most ${MAX_TEXT_LENGTH} characters`),
    company: yup
        .string()
        .trim()
        .required('Company or organization is required')
        .max(MAX_TEXT_LENGTH, `Company or organization must be at most ${MAX_TEXT_LENGTH} characters`),
    startMonth: yup.string().required('Start month is required'),
    startYear: yup
        .string()
        .required('Start year is required')
        .test('not-future', 'Start date cannot be in the future', function (startYear) {
            const {startMonth} = this.parent
            if (!startMonth || !startYear) return true
            return !isFuture(startYear, startMonth)
        }),
    currentlyWorking: yup.boolean().default(false),
    endMonth: yup.string().when('currentlyWorking', {
        is: false,
        then: s => s.required('End month is required'),
    }),
    endYear: yup
        .string()
        .when('currentlyWorking', {
            is: false,
            then: s => s.required('End year is required'),
        })
        .test('after-start', 'End date cannot be before start date', function (endYear) {
            const {currentlyWorking, startMonth, startYear, endMonth} = this.parent
            if (currentlyWorking || !startMonth || !startYear || !endMonth || !endYear) return true
            return toIndex(endYear, endMonth) >= toIndex(startYear, startMonth)
        })
        .test('not-future', 'End date cannot be in the future', function (endYear) {
            const {currentlyWorking, endMonth} = this.parent
            if (currentlyWorking || !endMonth || !endYear) return true
            return !isFuture(endYear, endMonth)
        }),
})

// Add or edit one work experience. The result is handed back through onSave;
// the owner of the list saves it.
export default ({open, experience, onClose, onSave}) => {
    const form = useForm({resolver: yupResolver(schema), defaultValues: emptyExperience})
    const currentlyWorking = form.watch('currentlyWorking')
    const errors = form.formState.errors

    useEffect(() => {
        if (open) form.reset(experience ? {...emptyExperience, ...experience} : emptyExperience)
    }, [open, experience])

    useEffect(() => {
        if (currentlyWorking) {
            form.setValue('endMonth', '')
            form.setValue('endYear', '')
            form.clearErrors(['endMonth', 'endYear'])
        }
    }, [currentlyWorking])

    const submit = e => {
        // The modal is portalled, but React still bubbles submit to any parent form.
        e.stopPropagation()
        return form.handleSubmit(data => onSave({...experience, ...data}))(e)
    }

    const renderSelect = (name, placeholder, options, ariaLabel) => (
        <FormControl fullWidth error={!!errors[name]}>
            <Controller
                name={name}
                control={form.control}
                render={({field}) => (
                    <Select
                        {...field}
                        value={field.value ?? ''}
                        onChange={e => field.onChange(e.target.value)}
                        displayEmpty
                        inputProps={{'aria-label': ariaLabel}}
                        MenuProps={{PaperProps: {style: {maxHeight: 300}}}}
                        renderValue={selected => {
                            if (selected === '') return <span style={{color: '#aaa'}}>{placeholder}</span>
                            return options.find(option => option.value === selected)?.label ?? selected
                        }}>
                        {options.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            />
            {errors[name] && <FormHelperText>{errors[name].message}</FormHelperText>}
        </FormControl>
    )

    const monthOptions = months.map((label, i) => ({value: String(i + 1), label}))
    const yearOptions = years.map(year => ({value: String(year), label: String(year)}))

    const renderDate = (prefix, label) => (
        <div className="field">
            <Typography variant="inputLabel" color="text.secondary">
                {label}
            </Typography>
            <div className="date">
                {renderSelect(`${prefix}Month`, 'Month', monthOptions, `${label} month`)}
                {renderSelect(`${prefix}Year`, 'Year', yearOptions, `${label} year`)}
            </div>
        </div>
    )

    return (
        <Modal open={open} onClose={onClose}>
            <form className="container" onSubmit={submit} noValidate>
                <div className="content">
                    <Typography variant="h6" color="text.primary" className="title">
                        {experience ? 'Edit Experience' : 'Add Experience'}
                    </Typography>
                    <div className="field">
                        <Typography variant="inputLabel" color="text.secondary" component="label" htmlFor="experience-job-title">
                            Job Title
                        </Typography>
                        <TextField
                            id="experience-job-title"
                            placeholder="Enter job title"
                            fullWidth
                            {...form.register('jobTitle')}
                            error={!!errors.jobTitle}
                            helperText={errors.jobTitle?.message}
                        />
                    </div>
                    <div className="field">
                        <Typography variant="inputLabel" color="text.secondary" component="label" htmlFor="experience-company">
                            Company or Organization
                        </Typography>
                        <TextField
                            id="experience-company"
                            placeholder="Enter your company"
                            fullWidth
                            {...form.register('company')}
                            error={!!errors.company}
                            helperText={errors.company?.message}
                        />
                    </div>
                    {renderDate('start', 'Start Date')}
                    <Controller
                        name="currentlyWorking"
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                className="checkbox"
                                control={<Checkbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />}
                                label="I am currently working in this role"
                            />
                        )}
                    />
                    {!currentlyWorking && renderDate('end', 'End Date')}
                    <div className="actions">
                        <Button variant="outlined" fullWidth onClick={onClose}>
                            Cancel
                        </Button>
                        <Button variant="contained" fullWidth type="submit">
                            {experience ? 'Save Changes' : 'Add Experience'}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    )
}
